import React, { useEffect, useMemo, useState } from 'react';
import {
  FaBars,
  FaBell,
  FaBookOpen,
  FaChevronLeft,
  FaChevronRight,
  FaClipboardCheck,
  FaCloudSun,
  FaCrown,
  FaFileContract,
  FaHome,
  FaMicrochip,
  FaMoon,
  FaQuestionCircle,
  FaSatellite,
  FaSeedling,
  FaShieldAlt,
  FaSignOutAlt,
  FaSlidersH,
  FaSun,
  FaTimes,
  FaTint,
  FaTree,
  FaUser,
  FaUserShield,
  FaUserFriends,
} from 'react-icons/fa';
import {
  getLocalOperationalUnreadCount,
  deduplicateOperationalNotifications,
  subscribeLocalOperationalNotifications,
} from '../services/operationalNotificationService';
import { cadernoRuntime, listCadernoNotifications, listIrrigationNotifications } from '../services/cadernoService';
import { loadVisibleAuthorizedProperties } from '../services/authorizedPropertiesService';
import CentralAjuda from './CentralAjuda';
import { getPlanHomeTarget, isModuleAllowed } from '../config/modulePermissions';
import { collaborationEnabled } from '../hooks/useCollaborationAccess';
import './AuthenticatedBottomNavigation.css';

const NAVIGATION_GROUPS = [
  {
    label: 'Visão geral',
    items: [
      { key: 'dashboard', label: 'Dashboard', description: 'Prontidão da operação', icon: FaHome, always: true },
      { key: 'notificacoes-operacionais', label: 'Notificações', description: 'Tarefas e aprovações', icon: FaBell, badge: true },
    ],
  },
  {
    label: 'Operação',
    items: [
      { key: 'sensoriamento', label: 'Central de Operações', description: 'Gestão territorial no mapa', icon: FaSatellite },
      { key: 'caderno', label: 'Caderno de Campo', description: 'Ordens e rastreabilidade', icon: FaClipboardCheck },
      { key: 'propriedades', label: 'Propriedades e Talhões', description: 'Áreas e safras', icon: FaTree },
      { key: 'alertas', label: 'Alertas', description: 'Riscos e ocorrências', icon: FaBell },
    ],
  },
  {
    label: 'Monitoramento',
    items: [
      { key: 'previsao', label: 'Previsão Climática', description: 'Clima e riscos', icon: FaCloudSun },
      { key: 'sensores', label: 'Sensores', description: 'Telemetria de campo', icon: FaMicrochip },
      { key: 'irrigacao', label: 'Irrigação', description: 'Manejo hídrico', icon: FaTint },
    ],
  },
  {
    label: 'Gestão',
    items: [
      { key: 'produtividade', label: 'Desempenho da Safra', description: 'Metas, custos e resultados', icon: FaSeedling },
      { key: 'pecuaria', label: 'Pecuária', description: 'Pastagens, lotes e manejo', icon: FaTree },
      { key: 'colaboracao', label: 'Colaboração e Equipe', description: 'Convites e acessos recebidos', icon: FaUserFriends },
    ],
  },
];

const HELP_CATEGORY_BY_SCREEN = {
  dashboard: 'inicio',
  caderno: 'caderno',
  alertas: 'alertas',
  propriedades: 'propriedades',
  previsao: 'clima',
  sensores: 'sensores',
  irrigacao: 'irrigacao',
  sensoriamento: 'satelite',
  'migracao-talhoes': 'propriedades',
  perfil: 'conta',
  planos: 'planos',
};

export default function AuthenticatedBottomNavigation({
  activeScreen,
  darkMode,
  setDarkMode,
  user,
  allowedModules = [],
  onNavigate,
  onLogout,
}) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('climorax:sidebar-collapsed') === 'true');
  const [operationalUnread, setOperationalUnread] = useState(() => getLocalOperationalUnreadCount(user?.id));
  const allowedSet = useMemo(() => new Set(allowedModules || []), [allowedModules]);
  const homeTarget = getPlanHomeTarget(user?.plano, user?.is_admin);
  const sensorOnly = !user?.is_admin && user?.plano === 'sensor_free';

  const groups = useMemo(() => {
    const visibleGroups = NAVIGATION_GROUPS.map(group => ({
      ...group,
      items: group.items.filter(item => (
        (item.key !== 'colaboracao' || collaborationEnabled)
        && isModuleAllowed(item.key, allowedSet, user?.is_admin, user?.plano)
      )),
    })).filter(group => group.items.length > 0);

    if (user?.is_admin) {
      visibleGroups.push({
        label: 'Administração',
        items: [{ key: 'admin', label: 'Painel Admin', description: 'Gestão da plataforma', icon: FaUserShield, always: true }],
      });
    }
    return visibleGroups;
  }, [allowedSet, user?.is_admin, user?.plano]);

  useEffect(() => {
    if (sensorOnly) {
      setOperationalUnread(0);
      return undefined;
    }
    if (cadernoRuntime.readsEnabled) {
      let cancelled = false;
      const updateRealCount = async () => {
        try {
          const [cadernoResult, irrigationResult, visiblePropertiesResult] = await Promise.allSettled([
            listCadernoNotifications(),
            listIrrigationNotifications(),
            loadVisibleAuthorizedProperties(user?.id, 'id'),
          ]);
          const cadernoRows = cadernoResult.status === 'fulfilled' ? cadernoResult.value : [];
          const irrigationRows = irrigationResult.status === 'fulfilled' ? irrigationResult.value : [];
          // Propriedades excluídas (arquivadas) não devem contar como pendência.
          const visiblePropertyIds = visiblePropertiesResult.status === 'fulfilled'
            ? new Set(visiblePropertiesResult.value.properties.map(property => String(property.id)))
            : null;
          const visibleCadernoRows = visiblePropertyIds
            ? cadernoRows.filter(row => !row.propriedade_id || visiblePropertyIds.has(String(row.propriedade_id)))
            : cadernoRows;
          const visibleIrrigationRows = visiblePropertyIds
            ? irrigationRows.filter(row => !row.dados?.propriedade_id || visiblePropertyIds.has(String(row.dados.propriedade_id)))
            : irrigationRows;
          if (!cancelled) {
            const unread = deduplicateOperationalNotifications([
              ...visibleCadernoRows.map(row => ({ ...row, read: Boolean(row.lida_em) })),
              ...visibleIrrigationRows.map(row => ({ ...row, read: Boolean(row.lida) })),
            ]).filter(row => !row.read).length;
            setOperationalUnread(unread);
          }
        } catch {
          if (!cancelled) setOperationalUnread(0);
        }
      };
      updateRealCount();
      const interval = setInterval(updateRealCount, 30000);
      return () => { cancelled = true; clearInterval(interval); };
    }

    const updateCount = () => setOperationalUnread(getLocalOperationalUnreadCount(user?.id));
    updateCount();
    return subscribeLocalOperationalNotifications(user?.id, updateCount);
  }, [sensorOnly, user?.id]);

  useEffect(() => {
    setDrawerOpen(false);
    setAccountMenuOpen(false);
  }, [activeScreen]);

  useEffect(() => {
    localStorage.setItem('climorax:sidebar-collapsed', String(collapsed));
    document.body.classList.toggle('authenticated-sidebar-collapsed', collapsed);
    return () => document.body.classList.remove('authenticated-sidebar-collapsed');
  }, [collapsed]);

  useEffect(() => {
    if (!drawerOpen) return undefined;
    const closeOnEscape = event => {
      if (event.key === 'Escape') setDrawerOpen(false);
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [drawerOpen]);

  const navigate = target => {
    setDrawerOpen(false);
    setAccountMenuOpen(false);
    onNavigate(target);
  };

  const navigateAccount = section => {
    setDrawerOpen(false);
    setAccountMenuOpen(false);
    onNavigate('perfil', { section });
  };

  const userInitials = (user?.nome || user?.email || 'U')
    .split(/\s+/)
    .slice(0, 2)
    .map(part => part[0])
    .join('')
    .toUpperCase();

  const firstName = user?.nome?.trim()?.split(/\s+/)[0] || 'Minha conta';

  const renderItem = item => {
    const Icon = item.icon;
    const active = activeScreen === item.key;
    return (
      <button
        type="button"
        key={item.key}
        className={`authenticated-sidebar-item ${active ? 'is-active' : ''}`}
        aria-current={active ? 'page' : undefined}
        onClick={() => navigate(item.key)}
        title={collapsed ? item.label : undefined}
      >
        <span className="authenticated-sidebar-item-icon"><Icon />{item.badge && operationalUnread > 0 && <em>{operationalUnread > 9 ? '9+' : operationalUnread}</em>}</span>
        <span className="authenticated-sidebar-item-copy"><strong>{item.label}</strong><small>{item.description}</small></span>
      </button>
    );
  };

  return (
    <>
      <div className={`authenticated-account-utility ${darkMode ? 'is-dark' : ''}`}>
        <span className="authenticated-account-context"><FaShieldAlt /> Ambiente protegido da sua conta</span>
        <button type="button" className="authenticated-account-quick" onClick={() => setHelpOpen(true)} aria-label="Abrir ajuda deste módulo" title="Ajuda e tutoriais"><FaQuestionCircle /></button>
        <button type="button" className="authenticated-account-quick" onClick={() => setDarkMode?.(value => !value)} aria-label={darkMode ? 'Ativar tema claro' : 'Ativar tema escuro'} title={darkMode ? 'Tema claro' : 'Tema escuro'}>{darkMode ? <FaSun /> : <FaMoon />}</button>
        <button type="button" className="authenticated-account-trigger" onClick={() => setAccountMenuOpen(value => !value)} aria-haspopup="menu" aria-expanded={accountMenuOpen}>
          <span>{userInitials}</span>
          <div><strong>{firstName}</strong><small>Conta e configurações</small></div>
          <FaChevronRight className={accountMenuOpen ? 'is-open' : ''} />
        </button>
      </div>

      <header className={`authenticated-mobile-header ${darkMode ? 'is-dark' : ''}`}>
        <button type="button" onClick={() => setDrawerOpen(true)} aria-label="Abrir menu de módulos"><FaBars /></button>
        <button type="button" className="authenticated-mobile-brand" onClick={() => navigate(homeTarget)} aria-label={sensorOnly ? 'Abrir Sensores' : 'Abrir Dashboard'}>
          <span>CX</span><strong>ClimoraX</strong>
        </button>
        {!sensorOnly && (
          <button type="button" className="authenticated-mobile-notifications" onClick={() => navigate('notificacoes-operacionais')} aria-label={`Abrir notificações${operationalUnread ? `, ${operationalUnread} não lidas` : ''}`}>
            <FaBell />{operationalUnread > 0 && <em>{operationalUnread > 9 ? '9+' : operationalUnread}</em>}
          </button>
        )}
        <button type="button" className="authenticated-mobile-account" onClick={() => setAccountMenuOpen(value => !value)} aria-label="Abrir minha conta" aria-expanded={accountMenuOpen}>{userInitials}</button>
      </header>

      {drawerOpen && <button type="button" className="authenticated-sidebar-backdrop" onClick={() => setDrawerOpen(false)} aria-label="Fechar menu de módulos" />}

      <aside className={`authenticated-sidebar ${darkMode ? 'is-dark' : ''} ${collapsed ? 'is-collapsed' : ''} ${drawerOpen ? 'is-open' : ''}`} aria-label="Menu principal do ClimoraX">
        <div className="authenticated-sidebar-heading">
          <button type="button" className="authenticated-sidebar-brand" onClick={() => navigate(homeTarget)} aria-label={sensorOnly ? 'Abrir Sensores' : 'Abrir Dashboard'}>
            <span>CX</span>
            <div><strong>ClimoraX</strong><small>Gestão agrícola</small></div>
          </button>
          <button type="button" className="authenticated-sidebar-close" onClick={() => setDrawerOpen(false)} aria-label="Fechar menu"><FaTimes /></button>
        </div>

        <nav className="authenticated-sidebar-navigation" aria-label="Módulos disponíveis">
          {groups.map(group => (
            <section key={group.label}>
              <h2>{group.label}</h2>
              <div>{group.items.map(renderItem)}</div>
            </section>
          ))}
        </nav>

        <div className="authenticated-sidebar-footer">
          <button type="button" className="authenticated-sidebar-user" onClick={() => setAccountMenuOpen(value => !value)} aria-label="Abrir minha conta">
            <span>{userInitials}</span>
            <div><strong>{user?.nome || 'Usuário ClimoraX'}</strong><small>{user?.email || 'Conta autenticada'}</small></div>
          </button>
          <div className="authenticated-sidebar-footer-actions">
            <button type="button" onClick={() => setDarkMode?.(value => !value)} title={darkMode ? 'Ativar tema claro' : 'Ativar tema escuro'}>{darkMode ? <FaSun /> : <FaMoon />}<span>{darkMode ? 'Tema claro' : 'Tema escuro'}</span></button>
            <button type="button" className="is-logout" onClick={onLogout} title="Sair da conta"><FaSignOutAlt /><span>Sair</span></button>
          </div>
        </div>

        <button type="button" className="authenticated-sidebar-collapse" onClick={() => setCollapsed(value => !value)} aria-label={collapsed ? 'Expandir menu lateral' : 'Recolher menu lateral'}>
          {collapsed ? <FaChevronRight /> : <><FaChevronLeft /><span>Recolher menu</span></>}
        </button>
      </aside>

      {accountMenuOpen && (
        <>
          <button type="button" className="authenticated-account-backdrop" onClick={() => setAccountMenuOpen(false)} aria-label="Fechar menu da conta" />
          <section className={`authenticated-account-menu ${darkMode ? 'is-dark' : ''}`} role="menu" aria-label="Conta e configurações">
            <header>
              <span>{userInitials}</span>
              <div><strong>{user?.nome || 'Usuário ClimoraX'}</strong><small>{user?.email || 'Conta autenticada'}</small></div>
            </header>
            <div className="authenticated-account-menu-group">
              <button type="button" role="menuitem" onClick={() => navigateAccount('perfil')}><FaUser /><span><strong>Minha conta</strong><small>Dados pessoais e preferências</small></span></button>
              {!sensorOnly && <button type="button" role="menuitem" onClick={() => navigateAccount('plano')}><FaCrown /><span><strong>Plano e utilização</strong><small>Assinatura, limites e recursos</small></span></button>}
              <button type="button" role="menuitem" onClick={() => navigateAccount('seguranca')}><FaShieldAlt /><span><strong>Segurança e dispositivos</strong><small>Acessos autorizados da conta</small></span></button>
              <button type="button" role="menuitem" onClick={() => navigateAccount('preferencias')}><FaSlidersH /><span><strong>Preferências</strong><small>Aparência e experiência</small></span></button>
            </div>
            <div className="authenticated-account-menu-group">
              <button type="button" role="menuitem" onClick={() => { setAccountMenuOpen(false); setHelpOpen(true); }}><FaBookOpen /><span><strong>Ajuda e tutoriais</strong><small>Orientações do módulo atual</small></span></button>
              <button type="button" role="menuitem" onClick={() => navigateAccount('legal')}><FaFileContract /><span><strong>Documentos legais</strong><small>Termos e políticas vigentes</small></span></button>
            </div>
            <button type="button" role="menuitem" className="authenticated-account-logout" onClick={onLogout}><FaSignOutAlt /> Sair da conta</button>
          </section>
        </>
      )}

      {helpOpen && <CentralAjuda initialCategory={HELP_CATEGORY_BY_SCREEN[activeScreen]} onClose={() => setHelpOpen(false)} />}
    </>
  );
}
